import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, Printer } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const categories = [
  { v: "hotel", l: "إيجار فندق" },
  { v: "bus", l: "باص" },
  { v: "supervisor", l: "مشرف" },
  { v: "prizes", l: "جوائز" },
  { v: "other", l: "أخرى" },
];
const catLabel = Object.fromEntries(categories.map((c) => [c.v, c.l]));

const empty = { category: "hotel", amount: "", date: new Date().toISOString().slice(0, 10), campaign_id: "", notes: "" };
const fmt = (n) => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 }).format(n || 0);

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [campaignFilter, setCampaignFilter] = useState("all");

  const load = async () => {
    try {
      const params = {};
      if (campaignFilter && campaignFilter !== "all") params.campaign_id = campaignFilter;
      const [e, c] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/campaigns", { params: { portal: "commercial" } }),
      ]);
      setItems(e.data); setCampaigns(c.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignFilter]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", {
        ...form,
        amount: Number(form.amount),
        campaign_id: form.campaign_id || null,
      });
      toast.success("تم تسجيل المصروف");
      setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا المصروف؟")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cName = useMemo(() => {
    const map = new Map(campaigns.map((c) => [c.id, `#${c.number} ${c.name}`]));
    return (id) => (id ? (map.get(id) || "—") : "—");
  }, [campaigns]);

  const total = items.reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        title="المصروفات"
        subtitle={`الإجمالي: ${fmt(total)} ر.س — عدد السجلات: ${items.length}`}
        actions={
          <>
            <Button
              data-testid="expenses-print-btn"
              variant="outline"
              onClick={() => window.open("/print/commercial/expenses", "_blank")}
              className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
            >
              <Printer className="h-4 w-4 ml-2" /> طباعة
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="expense-add-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                  <Plus className="h-4 w-4 ml-2" /> مصروف جديد
                </Button>
              </DialogTrigger>
              <DialogContent dir="rtl" className="max-w-lg">
                <DialogHeader><DialogTitle className="text-right">تسجيل مصروف</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  <div><Label>الفئة</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="expense-form-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map((c) => (<SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>المبلغ (ر.س)</Label>
                    <Input type="number" step="0.01" data-testid="expense-form-amount"
                      value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
                  </div>
                  <div><Label>التاريخ</Label>
                    <Input type="date" data-testid="expense-form-date"
                      value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div><Label>الحملة (اختياري)</Label>
                    <Select value={form.campaign_id || "none"} onValueChange={(v) => setForm({ ...form, campaign_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="expense-form-campaign"><SelectValue placeholder="بدون ربط" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">بدون ربط</SelectItem>
                        {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>ملاحظات</Label>
                    <Textarea data-testid="expense-form-notes" value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="expense-form-save"
                      className="bg-emerald-900 hover:bg-emerald-800 text-white">حفظ</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Card className="p-4 mb-4 border-emerald-900/10">
        <div className="max-w-sm">
          <Label className="text-sm text-slate-600 mb-1 block">فلترة بالحملة</Label>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger data-testid="expense-campaign-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع المصروفات</SelectItem>
              {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-emerald-900/10 overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-16 text-center">
            <Receipt className="h-10 w-10 text-emerald-900/30 mx-auto mb-3" />
            <p className="text-slate-500">لا توجد مصروفات مسجلة.</p>
          </div>
        ) : (
          <table className="w-full text-right min-w-[700px]" data-testid="expenses-table">
            <thead className="bg-emerald-950 text-white">
              <tr>
                <th className="p-3 font-heading">التاريخ</th>
                <th className="p-3 font-heading">الفئة</th>
                <th className="p-3 font-heading">الحملة</th>
                <th className="p-3 font-heading">المبلغ</th>
                <th className="p-3 font-heading">ملاحظات</th>
                <th className="p-3 font-heading w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((x, i) => (
                <tr key={x.id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="p-3 tabular-nums">{x.date}</td>
                  <td className="p-3"><span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-900 font-bold">{catLabel[x.category]}</span></td>
                  <td className="p-3 text-slate-700">{cName(x.campaign_id)}</td>
                  <td className="p-3 font-bold tabular-nums text-amber-700">{fmt(x.amount)}</td>
                  <td className="p-3 text-slate-600 text-sm">{x.notes || "—"}</td>
                  <td className="p-3">
                    <Button size="sm" variant="ghost" data-testid={`expense-delete-${x.id}`}
                      onClick={() => remove(x.id)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
